import {FormBuilder, Validators} from "@angular/forms"
import {inject, Injectable} from '@angular/core'
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"

import {Annotations} from "plotly.js"
import {
    BehaviorSubject,
    combineLatest,
    combineLatestWith,
    firstValueFrom,
    map,
    of,
    shareReplay,
    startWith,
    Subject,
    switchMap,
    tap
} from "rxjs"

import {EMPTY_FIGURE, VideFigure} from "../../../vide-types"
import {FileValidator} from "../../import-file/file-validator"
import {MeasureType} from "../../../api/measure-type"
import {PLOTLY_SCATTER_TYPE} from "../../../shared/vide-helper"
import {VideDataService} from "../../../api/vide-data.service"
import {
    ClickedPoint,
    DIVER_DATA_CODES,
    DiverFileData,
    DiverService,
    getLimitFilter,
    limitsToAnnotations,
    PlotLimits,
    readAsText
} from "../diver.service"
import {arrayNthValue} from "../../../shared/array-helper"
import {BasicObjectAndProject} from "../../../api/api-types"


@Injectable({
    providedIn: 'root'
})
export class ImportDiverService {
    private readonly fb = inject(FormBuilder)
    private readonly dataService = inject(VideDataService)
    private readonly diverService = inject(DiverService)

    readonly fileError = new Subject<string | null>()
    readonly form = this.fb.nonNullable.group({
        measureType: [null as null | MeasureType, Validators.required],
        file: [null as File | null, [Validators.required, FileValidator.nrFiles(1),]],
    })
    private _diverLimits = new BehaviorSubject<PlotLimits>({})
    readonly diverAnnotations$ = this._diverLimits.pipe(
        map(limitsToAnnotations),
    )
    private readonly selectedFile$ = this.form.controls.file.valueChanges.pipe(
        startWith(null),
        tap(() => {
            this.fileError.next(null)
        }),
    )
    private readonly rawDiverFileData$ = this.selectedFile$.pipe(
        switchMap(file => {
            if (!file) {
                return of(null)
            }
            return readAsText(file).then(text => this.diverService.parseDiverOfficeData(file.name, text)).catch(e => {
                console.error(e)
                if (e instanceof Error) this.fileError.next(e.message)
                return null
            })
        }),
    )
    readonly measureTypesForDiver$ = this.dataService.utility$.pipe(
        map(util => DIVER_DATA_CODES
            .map(code => util.getMeasureTypeByCode(code))
            .sort((a, b) => a.getDescription().localeCompare(b.getDescription()))),
    )

    readonly diverFileData$ = this.rawDiverFileData$.pipe(
        tap(_x => {
            this.resetDiverLimits()
        }),
        combineLatestWith(this.form.controls.measureType.valueChanges.pipe()),
        map(([data, tpe]) => {
            if (!data || !tpe) {
                return null
            }
            const ret: DiverFileData = {
                ...data,
                type: tpe
            }
            return ret
        }),
        shareReplay({refCount: true, bufferSize: 1}),
    )
    readonly diverFigure$ = combineLatest([
        this.diverFileData$,
        this.diverAnnotations$,
    ]).pipe(
        map(([x, annotations]) => {
            return x ? getFigure(x, annotations) : null
        }),
    )
    readonly diverClippedFileData$ = combineLatest([
        this.diverFileData$,
        this._diverLimits,
    ]).pipe(
        map(([x, limits]) => {
            if (!x) return null
            const clippedData = x.measurements.filter(getLimitFilter(limits))
            const ret: DiverFileData = {...x, measurements: clippedData}
            return ret
        }),
    )
    readonly diverNrMeasurements$ = this.diverClippedFileData$.pipe(
        map(data => data?.measurements.length),
    )


    constructor() {
        // Set the data type according to what we found in the file.
        // The user can change in the case of Baro-diver, as this looks as uncompensated data.
        this.rawDiverFileData$.pipe(takeUntilDestroyed()).subscribe(data => {
            const type = data?.type
            if (type) {
                this.form.patchValue({measureType: type})
            }
        })
        this.diverClippedFileData$.pipe(takeUntilDestroyed()).subscribe(data => {
            this.diverService.setFileData(data)
        })
        // Clear the selected file when a new object is selected.
        this.diverService.selectedObject$.pipe(takeUntilDestroyed()).subscribe(_object => {
            this.form.patchValue({file: null})
        })
    }

    resetDiverLimits() {
        this._diverLimits.next({})
    }

    setLimit(point: ClickedPoint, type: 'begin' | 'end') {
        const current = this._diverLimits.value
        const next = {
            ...current,
        }
        next[type] = point
        this._diverLimits.next(next)
    }

    async save(object: BasicObjectAndProject) {
        const interval = this.diverService.form.controls.saveInterval.value
        const localData = await firstValueFrom(this.diverClippedFileData$)
        const firstMeasurement = localData?.measurements.at(0)
        if (!localData || !firstMeasurement) {
            console.error('No input values')
            return
        }
        const measurements = arrayNthValue(localData.measurements, interval)
        const x = await this.diverService.saveToObject(measurements, {
            measureType: localData.type,
            object,
            update: false,
        })
        if (!x.success) {
            return
        }
        this.form.patchValue({file: null})
        this.dataService.reloadProjectData()

        const y = await this.diverService.createDiverNote(object, {
            measureTypeDescription: localData.type.getDescription(),
            first_date: firstMeasurement.dateTime,
            serial_number: localData?.serial,
        })
        if (!y.success) {
            return
        }
        return localData.type
    }

    async saveTemperature(object: BasicObjectAndProject) {
        const interval = this.diverService.form.controls.saveInterval.value
        const localData = await firstValueFrom(this.diverClippedFileData$)
        if (!localData || !localData.measurements.at(0)) {
            console.error('No input values')
            return
        }
        const measurements = arrayNthValue(localData.measurements, interval)
        const mt = await firstValueFrom(this.dataService.measureTypeTemperature$)
        const tempMeasurements = measurements.map(x => ({
            value: x.temp,
            dateTime: x.dateTime,
        }))
        return await this.diverService.saveToObject(tempMeasurements, {
            measureType: mt,
            object,
            update: false,
        })
    }
}

function getFigure(
    data: DiverFileData,
    annotations: Partial<Annotations>[],
) {
    let color
    switch (data.type.code) {
        case "mt_diver_uncompensated":
            color = DiverService.plotColors.uncompensated
            break
        case "mt_diver_unreferenced":
            color = DiverService.plotColors.unreferenced
            break
        case "mt_level_logger":
            color = DiverService.plotColors.result
            break
        case "mt_diver_pressure":
            color = DiverService.plotColors.pressure
            break
        default:
            console.error("Measure type not applicable", data.type)
            return EMPTY_FIGURE
        // assertNever(data.type.code)
    }
    const figure: VideFigure = {
        data: [
            {
                x: data.measurements.map(z => z.dateTime),
                y: data.measurements.map(z => z.value),
                yaxis: 'y',
                mode: 'lines+markers',
                type: PLOTLY_SCATTER_TYPE,
                name: data.type.getDescription(),
                marker: {color,},
            },
            {
                x: data.measurements.map(z => z.dateTime),
                y: data.measurements.map(z => z.temp),
                yaxis: 'y2',
                mode: 'lines+markers',
                type: PLOTLY_SCATTER_TYPE,
                name: 'temperature',
                marker: {color: DiverService.plotColors.temperature,},
            }

        ],
        layout: {
            autosize: true,
            yaxis: {
                title: {
                    // TODO: use the unit from the actual measure type!
                    text: data.type.outputUnit,
                    // text: 'meter H₂O',
                }
            },
            yaxis2: {
                title: {text: '°C',},
                overlaying: 'y',
                side: 'right',
            },
            annotations: annotations,
        },
        config: {
            // showLink: true, // text link on plot
            showEditInChartStudio: true, // icon in modebar
            plotlyServerURL: "https://chart-studio.plotly.com",
            // showLink: true,
            // linkText: 'This text is custom!' + imageSize?.width + 'x' + imageSize?.height,
            modeBarButtonsToRemove: [
                // 'pan2d',
                // 'zoom2d',
                'select2d',
                'lasso2d',
                'resetScale2d',
            ],
            modeBarButtonsToAdd: [
                'toggleSpikelines',
                // 'zoom2d',
            ],

            // Skip the size options, we use the default, the current plot size.
            // The size option is for the manual save button.
            // toImageButtonOptions: {filename: value.toImage.filename},
        }

    }
    return figure
}
